import type { ApplicationRouteEntry } from "./application-route"
import type { LocalizedMessage } from "../localization/localized-message"

export interface NavigationGroup {
  readonly key: string
  readonly title?: string | LocalizedMessage
  readonly icon?: string
  readonly routes: readonly ApplicationRouteEntry[]
}

/** Stable metadata ordering. Unspecified order is zero; ties retain declaration order. */
export function getNavigationGroups(routes: readonly ApplicationRouteEntry[]): readonly NavigationGroup[] {
  const groups = new Map<
    string,
    { key: string; title?: string | LocalizedMessage; icon?: string; routes: ApplicationRouteEntry[] }
  >()
  const ordered = routes
    .filter((route) => route.navigation != null)
    .sort((left, right) => (left.navigation?.order ?? 0) - (right.navigation?.order ?? 0))
  for (const route of ordered) {
    const title = route.navigation?.group
    const key =
      title == null
        ? "ungrouped"
        : typeof title === "string"
          ? JSON.stringify(["literal", title])
          : JSON.stringify([
              title.namespace,
              title.key,
              Object.entries(title.values).sort(([a], [b]) => a.localeCompare(b)),
            ])
    let group = groups.get(key)
    if (group == null) {
      group = { key, title, routes: [] }
      groups.set(key, group)
    }
    group.icon ??= route.navigation?.groupIcon
    group.routes.push(route)
  }
  return [...groups.values()]
}

/** The most specific matching navigation entry wins, including nested page routes. */
export function getActiveNavigationPath(
  routes: readonly ApplicationRouteEntry[],
  pathname: string
): string | undefined {
  const normalize = (path: string) => path.replace(/\/+$/, "") || "/"
  const path = normalize(pathname)
  return routes
    .filter((route) => route.navigation != null)
    .filter(
      (route) => path === normalize(route.path) || (route.path !== "/" && path.startsWith(`${normalize(route.path)}/`))
    )
    .sort((left, right) => right.path.length - left.path.length)[0]?.path
}
